import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';// Adjust path as needed
import "./CreateAccount.css";
import logo from "../components/assets/logo.png";

// Cookie Helper Functions
const CookieHelper = {
  setCookie: (name, value, days = 7) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  },

  getCookie: (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },

  deleteCookie: (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },

  saveLoginData: (userId, email, name, userType = 'user') => {
    CookieHelper.setCookie('isLoggedIn', 'true', 7);
    CookieHelper.setCookie('userId', userId, 7);
    CookieHelper.setCookie('userEmail', email, 7);
    CookieHelper.setCookie('userName', name, 7);
    CookieHelper.setCookie('userType', userType, 7);
  },

  isLoggedIn: () => {
    return CookieHelper.getCookie('isLoggedIn') === 'true';
  },

  clearLoginData: () => {
    CookieHelper.deleteCookie('isLoggedIn');
    CookieHelper.deleteCookie('userId');
    CookieHelper.deleteCookie('userEmail');
    CookieHelper.deleteCookie('userName');
    CookieHelper.deleteCookie('userType');
  }
};

const CreateAccount = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    password: "",
    passwordConfirm: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showpasswordConfirm, setShowpasswordConfirm] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    document.body.classList.add('create-account-active');

    // Check if user is already logged in
    const checkLoginStatus = async () => {
      if (CookieHelper.isLoggedIn()) {
        const userId = CookieHelper.getCookie('userId');
        if (userId) {
          // Verify user still exists in Firebase
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              navigate('/LandingPage');
              return;
            } else {
              CookieHelper.clearLoginData();
            }
          } catch (e) {
            console.error('Error verifying user:', e);
            CookieHelper.clearLoginData();
          }
        }
      }
      setIsCheckingAuth(false);
    };

    checkLoginStatus();

    return () => {
      document.body.classList.remove('create-account-active');
    };
  }, [navigate]);

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const validateForm = () => {
    if (!userData.name || !userData.email || !userData.password || !userData.passwordConfirm) {
      setError("Please fill in all fields");
      return false;
    }
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions");
      return false;
    }
    if (userData.name.trim().length < 2) {
      setError("Name must be at least 2 characters long");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (userData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    if (userData.password !== userData.passwordConfirm) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleCreateAccount = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError("");

    try {
      const { name, email, password } = userData;

      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);

      // Save user data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        name,
        email,
        profileImage: '',
        phone: '',
        location: '',
        emailVerified: false,
        signInMethod: 'email',
        createdAt: new Date(),
      });

      setTempUser(user);
      setEmailVerificationSent(true);
      toast.success("Account created! Please verify your email before logging in.");

      // Start checking for email verification
      checkEmailVerification(user);

    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists with this email');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError(err.message || "Account creation failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check Email Verification Status
  const checkEmailVerification = async (user) => {
    let checkCount = 0;
    const maxChecks = 60; // Check for 3 minutes (60 * 3 seconds)

    const checkInterval = setInterval(async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(checkInterval);
          
          // Update Firestore
          await setDoc(doc(db, "users", user.uid), {
            emailVerified: true,
          }, { merge: true });

          // Save to cookies
          CookieHelper.saveLoginData(
            user.uid,
            user.email,
            userData.name,
            'user'
          );

          toast.success('Email verified! Account created successfully.');
          
          // Navigate to login page
          setTimeout(() => {
            navigate("/Login");
          }, 2000);
        }
        
        checkCount++;
        if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
        }
      } catch (error) {
        console.error('Error checking email verification:', error);
      }
    }, 3000);
  };

  // Manual email verification check
  const handleCheckEmailVerification = async () => {
    if (!tempUser) return;

    setIsLoading(true);
    try {
      await tempUser.reload();
      if (tempUser.emailVerified) {
        // Update Firestore
        await setDoc(doc(db, "users", tempUser.uid), {
          emailVerified: true,
        }, { merge: true });

        // Save to cookies
        CookieHelper.saveLoginData(
          tempUser.uid,
          tempUser.email,
          userData.name,
          'user'
        );

        toast.success('Email verified! Account created successfully.');
        
        // Navigate to login page
        setTimeout(() => {
          navigate("/Login");
        }, 2000);
      } else {
        toast.error('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      toast.error('Error checking verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user document exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Create new user document
        await setDoc(userDocRef, {
          name: user.displayName || 'Google User',
          email: user.email,
          profileImage: user.photoURL || '',
          phone: '',
          location: '',
          emailVerified: true,
          signInMethod: 'google',
          createdAt: new Date()
        });
      }

      // Save to cookies
      CookieHelper.saveLoginData(
        user.uid,
        user.email,
        user.displayName || 'Google User',
        'user'
      );

      toast.success('Account created with Google successfully!');
      
      // Navigate to landing page
      setTimeout(() => {
        navigate("/LandingPage");
      }, 2000);

    } catch (error) {
      console.error('Google signup error:', error);
      setError('Google sign-up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookSignup = () => {
    toast.info("Facebook signup will be implemented soon!", { autoClose: 3000 });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const togglepasswordConfirmVisibility = () => {
    setShowpasswordConfirm(!showpasswordConfirm);
  };

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="create-account-page-body">
        <div className="create-account-container">
          <div className="create-account-loading-screen">
            <img src={logo} alt="LitStudio" className="create-account-logo" />
            <div className="create-account-spinner"></div>
            <p>Checking authentication status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-account-page-body">
      <div className="create-account-container">
        {/* Editorial panel */}
        <div className="create-account-left">
          <img src="/images/event-setup.jpg" alt="" />
          {/* <img src={logo} alt="LitStudio" className="create-account-logo" /> */}

          <button
            type="button"
            className="create-account-back-button"
            onClick={() => navigate("/LandingPage")}
          >
            <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Back to website
          </button>

          <div>
            <div className="create-account-caption">Book photographers you can actually trust.</div>
            <p className="create-account-caption-sub">
              Every photographer is verified before their portfolio goes live, and
              your payment is held securely until the shoot is delivered.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <div className="create-account-right">
          <div className="create-account-panel">
            {emailVerificationSent ? (
              <div className="create-account-verification-section">
                <div className="create-account-verification-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m2 7 10 6 10-6" />
                  </svg>
                </div>

                <h2>Verify your email</h2>
                <p className="create-account-verification-text">
                  We have sent a verification link to{" "}
                  <strong>{userData.email}</strong>
                </p>

                <div className="create-account-verification-info">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    Open the link in your inbox, then come back here and confirm below.
                  </span>
                </div>

                {error && (
                  <div className="create-account-error" role="alert">
                    <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  className="create-account-submit-btn"
                  type="button"
                  onClick={handleCheckEmailVerification}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="create-account-icon create-account-icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <path d="M21 12a9 9 0 1 1-6.2-8.6" />
                      </svg>
                      Checking...
                    </>
                  ) : (
                    "I have verified my email"
                  )}
                </button>

                <button
                  type="button"
                  className="create-account-back-to-form-btn"
                  onClick={() => setEmailVerificationSent(false)}
                >
                  Back to sign up
                </button>
              </div>
            ) : (
              <>
                <span className="create-account-badge">
                  <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Client
                </span>

                <h2>Create your account</h2>
                <p className="create-account-subtitle">
                  Already have an account?{" "}
                  <button type="button" className="create-account-link-inline" onClick={() => navigate("/UserLogin")}>
                    Log in
                  </button>
                </p>

                {error && (
                  <div className="create-account-error" role="alert">
                    <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <form className="create-account-form" onSubmit={(e) => e.preventDefault()}>
                  <div className="create-account-form-group">
                    <label htmlFor="name">Full name</label>
                    <input
                      id="name"
                      className="create-account-input"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      autoComplete="name"
                      value={userData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="create-account-form-group">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      className="create-account-input"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={userData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="create-account-form-group">
                    <label htmlFor="password">Password</label>
                    <div className="create-account-password-wrap">
                      <input
                        id="password"
                        className="create-account-input"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="At least 6 characters"
                        autoComplete="new-password"
                        value={userData.password}
                        onChange={handleChange}
                        required
                      />
                      <button
                        type="button"
                        className="create-account-password-toggle"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                          </svg>
                        ) : (
                          <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="create-account-form-group">
                    <label htmlFor="passwordConfirm">Confirm password</label>
                    <div className="create-account-password-wrap">
                      <input
                        id="passwordConfirm"
                        className="create-account-input"
                        name="passwordConfirm"
                        type={showpasswordConfirm ? "text" : "password"}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        value={userData.passwordConfirm}
                        onChange={handleChange}
                        required
                      />
                      <button
                        type="button"
                        className="create-account-password-toggle"
                        onClick={togglepasswordConfirmVisibility}
                        aria-label={showpasswordConfirm ? "Hide password" : "Show password"}
                      >
                        {showpasswordConfirm ? (
                          <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                          </svg>
                        ) : (
                          <svg className="create-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="create-account-checkbox-container">
                    <input
                      id="create-terms"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                    />
                    <label htmlFor="create-terms">
                      I agree to the{" "}
                      <button type="button" onClick={() => navigate("/AboutUs")}>Terms</button>{" "}
                      and{" "}
                      <button type="button" onClick={() => navigate("/AboutUs")}>Privacy Policy</button>
                    </label>
                  </div>

                  <button
                    className="create-account-submit-btn"
                    type="button"
                    onClick={handleCreateAccount}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <svg className="create-account-icon create-account-icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <path d="M21 12a9 9 0 1 1-6.2-8.6" />
                        </svg>
                        Creating account...
                      </>
                    ) : (
                      "Create account"
                    )}
                  </button>
                </form>

                <div className="create-account-divider">or</div>

                <div className="create-account-social-login">
                  <button className="create-account-social-btn" onClick={handleGoogleSignup} disabled={isLoading}>
                    <svg viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5Z" />
                      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.7 7l7.6 5.9c4.4-4.1 6.8-10.8 6.8-17.4Z" />
                      <path fill="#FBBC05" d="M10.4 28.7a14.6 14.6 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1Z" />
                      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.4 0-11.7-3.7-13.6-8.9l-7.8 6.1C6.5 42.6 14.6 48 24 48Z" />
                    </svg>
                    Continue with Google
                  </button>
                </div>

                <p className="create-account-terms">
                  Looking to join as a photographer?{" "}
                  <button type="button" onClick={() => navigate("/personal")}>Sign up here</button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={4000} hideProgressBar newestOnTop closeOnClick pauseOnHover theme="light" />
    </div>
  );
};

export default CreateAccount;
